import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../data/gameConstants';
import { InputSystem } from '../systems/InputSystem';
import { ResponsiveSystem } from '../systems/ResponsiveSystem';
import { UIStyle } from './UIStyle';
import { setZoneHeaderVisible } from './ZoneHudHeader';
import { AudioSystem } from '../systems/AudioSystem';

export interface DecisionOption {
  id: string;
  label: string;
}

export class DecisionPanel {
  private scene: Phaser.Scene;
  private container?: Phaser.GameObjects.Container;
  private overlay?: Phaser.GameObjects.Rectangle;
  private visible = false;
  private optionIds: string[] = [];
  private onSelect?: (id: string) => void;
  private key1?: Phaser.Input.Keyboard.Key;
  private key2?: Phaser.Input.Keyboard.Key;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    if (scene.input.keyboard) {
      this.key1 = scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ONE);
      this.key2 = scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.TWO);
    }
  }

  isVisible(): boolean {
    return this.visible;
  }

  show(
    title: string,
    question: string,
    options: DecisionOption[],
    onSelect: (id: string) => void,
  ): void {
    this.hide();
    this.onSelect = onSelect;
    this.optionIds = options.map((o) => o.id);
    this.visible = true;
    setZoneHeaderVisible(false);

    const isMobile = ResponsiveSystem.isMobile(this.scene);
    const safe = ResponsiveSystem.getSafeBounds(this.scene.game, {
      left: 20,
      right: 20,
      top: 20,
      bottom: isMobile ? 20 : 28,
    });

    const hasHeader = !!(title || question);
    const padX = isMobile ? 28 : 32;
    const padTop = hasHeader ? (isMobile ? 26 : 28) : (isMobile ? 20 : 22);
    const padBottom = hasHeader ? (isMobile ? 22 : 26) : (isMobile ? 18 : 20);
    const sectionGap = isMobile ? 22 : 26;
    const btnGap = isMobile ? 12 : 10;
    const minButtonHeight = isMobile ? 48 : 44;
    const btnPadY = isMobile ? 14 : 12;
    const panelW = Math.min(isMobile ? 560 : 520, safe.width);
    const innerW = panelW - padX * 2;
    const btnW = innerW;
    const btnLabelStyle = {
      ...UIStyle.buttonLabel(isMobile ? UIStyle.px(16) : UIStyle.px(18)),
      align: 'center' as const,
      wordWrap: { width: btnW - 28 },
      lineSpacing: 4,
    };

    const titleStyle = {
      ...UIStyle.panelTitle(isMobile ? UIStyle.px(22) : UIStyle.px(24)),
      align: 'center' as const,
      wordWrap: { width: innerW },
    };
    const questionStyle = {
      ...UIStyle.panelBody(isMobile ? UIStyle.px(17) : UIStyle.px(18)),
      align: 'center' as const,
      wordWrap: { width: innerW },
      lineSpacing: 8,
    };

    const titleProbe = title
      ? this.scene.add.text(0, 0, title, titleStyle)
      : null;
    const questionProbe = question
      ? this.scene.add.text(0, 0, question, questionStyle)
      : null;
    const titleH = titleProbe?.height ?? 0;
    const questionH = questionProbe?.height ?? 0;
    titleProbe?.destroy();
    questionProbe?.destroy();

    const titleBlock = titleH > 0 ? titleH + sectionGap : 0;
    const questionBlock = questionH > 0 ? questionH + sectionGap : 0;
    const buttonHeights = options.map((opt, i) => {
      const number = String(i + 1);
      const probe = this.scene.add.text(0, 0, `${number}. ${opt.label}`, btnLabelStyle);
      const h = Math.max(minButtonHeight, probe.height + btnPadY * 2);
      probe.destroy();
      return h;
    });
    const buttonsH = buttonHeights.reduce(
      (sum, h, i) => sum + h + (i < options.length - 1 ? btnGap : 0),
      0,
    );
    const contentH = padTop + titleBlock + questionBlock + buttonsH + padBottom;
    const panelH = Math.min(safe.height, contentH);

    const centerX = safe.centerX;
    const centerY = safe.centerY;

    this.overlay = this.scene.add.rectangle(
      GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.58,
    );
    this.overlay.setScrollFactor(0).setDepth(1500).setInteractive();

    this.container = this.scene.add.container(centerX, centerY);
    this.container.setScrollFactor(0).setDepth(1501);

    const panel = UIStyle.createModalSurface(this.scene, 0, 0, panelW, panelH, {
      showHeader: hasHeader,
    });

    let cursorY = -panelH / 2 + padTop;

    const panelChildren: Phaser.GameObjects.GameObject[] = [panel];

    if (title) {
      const titleText = this.scene.add.text(0, cursorY, title, titleStyle);
      titleText.setOrigin(0.5, 0);
      panelChildren.push(titleText);
      cursorY += titleH + sectionGap;
    }

    if (question) {
      const questionText = this.scene.add.text(0, cursorY, question, questionStyle);
      questionText.setOrigin(0.5, 0);
      panelChildren.push(questionText);
      cursorY += questionH + sectionGap;
    }

    const btnBgColor = 0x1a3048;
    const btnHoverColor = 0x243f5c;
    const btnStroke = 0xd4a373;
    const btnHoverStroke = 0xffd966;

    options.forEach((opt, i) => {
      const buttonHeight = buttonHeights[i] ?? minButtonHeight;
      const y = cursorY + buttonHeight / 2;

      const btnBg = this.scene.add.rectangle(0, y, btnW, buttonHeight, btnBgColor, 1);
      btnBg.setStrokeStyle(2, btnStroke, 0.85);
      btnBg.setInteractive({ useHandCursor: true });

      const number = String(i + 1);
      const btnLabel = this.scene.add.text(0, y, `${number}. ${opt.label}`, btnLabelStyle).setOrigin(0.5);
      btnLabel.setInteractive({ useHandCursor: true });

      const hoverOn = () => {
        btnBg.setStrokeStyle(3, btnHoverStroke, 1);
        btnBg.setFillStyle(btnHoverColor, 1);
      };
      const hoverOff = () => {
        btnBg.setStrokeStyle(2, btnStroke, 0.85);
        btnBg.setFillStyle(btnBgColor, 1);
      };

      btnBg.on('pointerover', hoverOn);
      btnBg.on('pointerout', hoverOff);
      btnBg.on('pointerup', () => this.select(opt.id));
      btnLabel.on('pointerover', hoverOn);
      btnLabel.on('pointerout', hoverOff);
      btnLabel.on('pointerup', () => this.select(opt.id));

      panelChildren.push(btnBg, btnLabel);
      cursorY += buttonHeight + (i < options.length - 1 ? btnGap : 0);
    });

    this.container.add(panelChildren);
  }

  handleInput(_input: InputSystem): void {
    if (!this.visible) return;

    if (this.key1 && Phaser.Input.Keyboard.JustDown(this.key1) && this.optionIds[0]) {
      this.select(this.optionIds[0]);
      return;
    }
    if (this.key2 && Phaser.Input.Keyboard.JustDown(this.key2) && this.optionIds[1]) {
      this.select(this.optionIds[1]);
    }
  }

  private select(id: string): void {
    if (!this.visible) return;
    AudioSystem.playSfx('DECISION');
    const callback = this.onSelect;
    this.hide();
    callback?.(id);
  }

  hide(): void {
    this.visible = false;
    this.optionIds = [];
    this.container?.destroy();
    this.overlay?.destroy();
    this.container = undefined;
    this.overlay = undefined;
    this.onSelect = undefined;
    setZoneHeaderVisible(true);
  }

  destroy(): void {
    this.hide();
  }
}
